'use client';

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useSelector } from 'react-redux';
import { Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/app/features/language/hooks/useTranslation';
import ThemeCustomizer from '../../features/theme/components/ThemeCustomizer';
import { LanguageSelector } from '../../features/language/components/LanguageSelector';
import KobottoLogo from '../../features/shared/components/KobottoLogo';
import DefaultTenantLogo from '../../features/shared/components/DefaultTenantLogo';
import AccountSwitcherDropdown from '../../features/auth/components/AccountSwitcherDropdown';
import { useBranding } from '../../hooks/useBranding';
import { useAuthTranslation } from '../../features/language/hooks/useTranslation';
import { useAuth, useAuthInitialization } from '../../hooks/useAuth';
import { NotificationBell } from '../../features/notifications/components/NotificationBell';
import { TenantSwitcher } from '../../features/navigation/components/TenantSwitcher';
import { ActivePartySelector } from '../../features/navigation/components/ActivePartySelector';
import Avatar from '../Avatar';
import ProfilePictureModal from '../../features/profile/components/ProfilePictureModal';
import { selectCurrentUser } from '../../store/slices/userSlice';
import { selectActiveTenantSubscribesToEdiModule } from '../../store/selectors/availableAppsSelectors';
import { IngestProgressBadge } from '../IngestProgressBadge';
import { useIngestBackgroundProgressBoot } from '../../features/pack-analysis-workbench/hooks/useIngestBackgroundProgressBoot';
import { useLocationPathname } from '../../features/workspace/routing/useWorkspacePath';

interface HeaderProps {
  autoHide?: boolean;
  compact?: boolean;
}

const Header: React.FC<HeaderProps> = ({ 
  autoHide = false, 
  compact = false 
}) => {
  const router = useRouter();
  // Live URL, not `usePathname()`: the Header is mounted INSIDE the workspace
  // shell too, where `/workspace/:path*` is rewritten to `/workspace` and
  // navigation goes through the History API — Next's router never sees the real
  // URL there. Spec: workspace-static-spa-routing.
  const pathname = useLocationPathname();
  const { t } = useAuthTranslation();
  const { t: tNav } = useTranslation('navigation');
  const branding = useBranding();

  // Use only auth system
  const auth = useAuth();
  const { isInitializing } = useAuthInitialization();

  // Active tenant drives workspace-vs-marketing routing decisions in this header.
  const activeTenantState = useSelector((state: any) => state.tenant.activeTenant);
  const tenantCompanyName: string | null = useSelector((state: any) => state.workspace.tenantCompanyName);
  // Tenant-resolved header-corner icon URL (spec: tenant-dual-logo); null ⇒ the
  // built-in Default Tenant logo. The corner uses ONLY this key — never the
  // page wordmark (branding.logo_url).
  const tenantLogoIconUrl: string | null = useSelector((state: any) => state.workspace.tenantLogoIconUrl);

  // Use auth system directly
  const isAuthenticated = auth.isAuthenticated;
  const user = auth.user;
  const reduxUser = useSelector(selectCurrentUser);

  // edi-module-registration: gate the Active Buyer/Supplier selector on the
  // active tenant's EDI module subscription. Derived from `availableApps.apps`
  // containing the EDI module row (BE includes it only when the tenant is
  // subscribed, since EDI is access_type='private'). Replaces the prior
  // implicit hide-when-empty behaviour of fetchMyBuyers.
  const subscribesToEdiModule = useSelector(selectActiveTenantSubscribesToEdiModule);

  // Spec pack-analysis-ingest-background-progress Req 5.4 — singleton boot
  // hook for global ingest progress side effects (toast + Browser Notification
  // on completion). Header is mounted on every authenticated page, so this
  // is the canonical mount point. The hook is a pure observer — it does not
  // touch the SSE subscription, only watches the activeBatch slice transition.
  useIngestBackgroundProgressBoot();

  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const openProfileModal = useCallback(() => {
    setDropdownOpen(false);
    setProfileModalOpen(true);
  }, []);
  const closeProfileModal = useCallback(() => setProfileModalOpen(false), []);

  // Auto-hide header functionality
  const [headerVisible, setHeaderVisible] = useState(true);
  const lastScrollYRef = useRef(0);
  const [isClient, setIsClient] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);
  const [isDark, setIsDark] = useState(false);
  // True once a configured tenant corner icon has failed to load — falls the
  // header corner back to the Default Tenant logo (spec: tenant-dual-logo, P2).
  const [tenantLogoError, setTenantLogoError] = useState(false);

  // Memoize user info to prevent unnecessary re-renders
  useMemo(() => {
    if (!user) return null;
    return {
      userName: user.name || user.email || 'Unknown User',
      loginName: user.email || 'unknown@example.com'
    };
  }, [user]);

  // Auto-hide header on scroll with hysteresis to prevent flipping
  useEffect(() => {
    if (!autoHide) return;

    // Must scroll this many px in the new direction (from the last toggle point)
    // before the header changes state. Prevents flip on tiny or oscillating scroll.
    const TOGGLE_THRESHOLD = 30;
    let rafId: number | null = null;
    let visible = true; // local mirror of headerVisible — avoids duplicate setState

    const handleScroll = () => {
      if (rafId !== null) return; // one update per animation frame
      rafId = requestAnimationFrame(() => {
        const y = window.scrollY;

        if (y < 50) {
          // Always show near the very top; reset the tracking anchor
          if (!visible) { visible = true; setHeaderVisible(true); }
          lastScrollYRef.current = y;
        } else {
          const delta = y - lastScrollYRef.current;
          if (Math.abs(delta) >= TOGGLE_THRESHOLD) {
            const shouldShow = delta < 0; // negative = scrolling up
            if (shouldShow !== visible) {
              visible = shouldShow;
              setHeaderVisible(shouldShow);
            }
            lastScrollYRef.current = y; // advance anchor only on a real toggle
          }
        }

        rafId = null;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [autoHide]);

  // Initialize client-side functionality (theme only - no auth!)
  useEffect(() => {
    setIsClient(true);
    
    // Check initial theme
    const checkTheme = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };
    
    checkTheme();
    
    // Listen for theme changes
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, []);

  // A new icon URL (e.g. after a tenant switch) gets a fresh load attempt.
  useEffect(() => { setTenantLogoError(false); }, [tenantLogoIconUrl]);

  const handleDropdownToggle = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setButtonRect(rect);
    setDropdownOpen(!dropdownOpen);
  }, [dropdownOpen]);

  const handleLogin = useCallback(() => router.push('/login'), [router]);

  // Whether the corner should render a TENANT identity vs the global operator
  // brand (spec: tenant-branding-identity, tenant-dual-logo).
  //
  // The corner is HONEST: it shows a tenant brand only when that brand is
  // verified to be the current tenant — i.e. we are signed in, an active tenant
  // is set, AND that tenant's company-name override has actually resolved
  // (`tenantCompanyName`). Anything short of that shows the global operator
  // brand:
  //   - the global landing (`/`) always renders the operator brand;
  //   - signed out / auth still resolving — a persisted `activeTenant`
  //     (localStorage) OUTLIVES an expired session, so trusting it here would
  //     show a stale/wrong tenant when the user isn't actually in it;
  //   - branding not yet loaded (or a tenant that configured no override) —
  //     we render the global brand rather than guess.
  // This deliberately never shows the registered legal name
  // (`activeTenant.tenantName`, e.g. "Tradelink Technologies Limited"): the
  // corner is the brand, not the legal entity name. Logo and name move together
  // off this single signal, so they can never disagree.
  const onGlobalLanding = pathname === '/';
  const showTenantBrand =
    !onGlobalLanding && isAuthenticated && !!activeTenantState && !!tenantCompanyName;

  const handleGoToHome = useCallback(() => {
    // The header brand-click goes to the landing page (spec: tenant-landing-page):
    // a tenant's own /welcome/{tenantCode} when the corner shows a tenant
    // identity, otherwise the global SmartMatrix landing at /. The landing's
    // "Explore Our Solution" button is the funnel back into the workspace.
    // Reuses the corner identity gate so the click target always matches the
    // brand being displayed.
    if (showTenantBrand) {
      router.push(`/welcome/${activeTenantState.tenantCode}`);
    } else {
      router.push('/');
    }
  }, [router, showTenantBrand, activeTenantState]);

  const handleDropdownClose = useCallback(() => setDropdownOpen(false), []);


  // Dynamic header height based on compact mode
  const headerHeight = compact ? 'h-10' : 'h-12';
  const headerPadding = 'px-4';

  // Header upper-left identity (spec: tenant-branding-identity, tenant-dual-logo).
  const logoSize = compact ? 20 : 24;
  // Display name: the tenant's resolved branding.company_name override; the
  // operator company name whenever the corner isn't showing a verified tenant
  // brand. (`tenantCompanyName` is guaranteed non-null when showTenantBrand.)
  const cornerName = showTenantBrand && tenantCompanyName ? tenantCompanyName : branding.companyName;
  // Corner icon: the tenant's branding.logo_icon_url image is shown only when a
  // verified tenant brand is on screen and the image loads. Otherwise the
  // built-in mark — the Default Tenant logo for a verified tenant, KobottoLogo
  // otherwise (spec: tenant-dual-logo).
  const showTenantLogo = showTenantBrand && !!tenantLogoIconUrl && !tenantLogoError;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-[999999] safe-top ${
          autoHide ? `transition-transform duration-300 ease-in-out ${headerVisible ? 'translate-y-0' : '-translate-y-full'}` : ''
        } ${headerHeight} ${headerPadding} backdrop-blur-md`}
        style={{ 
          background: 'linear-gradient(to right, hsl(var(--primary) / 0.95), hsl(var(--accent) / 0.95))', 
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <div className="flex items-center justify-between w-full h-full">
          {/* Left Section. Flex layout guarantees left/right sections can never
              overlap on narrow viewports (replaced prior dual-absolute layout).
              Logo icon left edge aligned to the AppShell activity bar icon
              left edge (~18px from viewport). "Kobotto" wordmark hidden on
              mobile (< sm) so the narrow viewport has full width for the
              right icon cluster — tenant name is already surfaced in the
              right-side TenantSwitcher pill. */}
          <div className="min-w-0">
            <div
              className="text-white cursor-pointer flex items-center gap-1.5 hover:opacity-80 transition-opacity"
              onClick={handleGoToHome}
            >
              {showTenantLogo ? (
                // A custom tenant corner icon renders larger than the built-in
                // mark — height-fixed, width-auto so it keeps its aspect ratio.
                <img
                  src={tenantLogoIconUrl!}
                  alt=""
                  style={{ height: compact ? 22 : 29 }}
                  className="w-auto object-contain"
                  onError={() => setTenantLogoError(true)}
                />
              ) : showTenantBrand ? (
                <DefaultTenantLogo size={logoSize} />
              ) : (
                <KobottoLogo size={logoSize} />
              )}
              <span className={`hidden sm:inline font-bold tracking-tight ${compact ? 'text-sm' : 'text-base'}`}>
                {cornerName}
              </span>
            </div>
          </div>

          {/* Right Section */}
          <div>
            <div className="flex items-center gap-2">
              {/* Tenant Switcher - subtle indicator, only visible when user has multiple tenants */}
              {isClient && !isInitializing && isAuthenticated && (
                <TenantSwitcher variant="header" />
              )}

              {/* Active-party selector — chained buyer/supplier working context.
                  Gated on the active tenant's EDI module subscription (spec:
                  edi-module-registration). Non-EDI tenants see nothing here. */}
              {isClient && !isInitializing && isAuthenticated && subscribesToEdiModule && (
                <ActivePartySelector />
              )}

              {/* Guest shortcut — jumps unauthenticated visitors to /main to browse public apps */}
              {isClient && !isInitializing && !isAuthenticated && (
                <button
                  type="button"
                  onClick={() => router.push('/main')}
                  title={tNav('home')}
                  aria-label={tNav('home')}
                  className="hidden md:flex items-center gap-1 text-white/70 hover:text-white text-xs rounded px-2 py-1 hover:bg-white/10 transition-colors mr-1"
                >
                  <Home size={14} className="shrink-0" />
                </button>
              )}

              <LanguageSelector size="small" />
              <ThemeCustomizer size="small" />

              {/* Notification Bell - Only show when authenticated */}
              {isClient && !isInitializing && isAuthenticated && (
                <NotificationBell />
              )}

              {/* Spec pack-analysis-ingest-background-progress — persistent
                  ingest-progress chip. Renders nothing when no batch is
                  active (Req 2.2 — zero footprint at rest), so this slot
                  collapses cleanly when not in use. */}
              {isClient && !isInitializing && isAuthenticated && (
                <IngestProgressBadge />
              )}
              
              {/* Profile/Login Button — visually nudged down 4px so the avatar
                  circle reads as settled within the header baseline rather than
                  sitting flush with the smaller icon buttons next to it. */}
              <div className="relative mt-1">
                {!isClient ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-8 h-8 p-0 rounded-full bg-white/10"
                    disabled
                  >
                    <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  </Button>
                ) : isInitializing ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-8 h-8 p-0 rounded-full bg-white/10"
                    disabled
                  >
                    <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  </Button>
                ) : isAuthenticated ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-8 h-8 p-0 rounded-full bg-white/10 hover:bg-white/20 transition-colors overflow-hidden"
                    onClick={handleDropdownToggle}
                  >
                    <Avatar
                      profilePictureUrl={reduxUser?.profilePictureUrl}
                      displayName={reduxUser?.displayName ?? user?.name}
                      givenName={reduxUser?.givenName ?? undefined}
                      surname={reduxUser?.surname ?? undefined}
                      size="sm"
                    />
                  </Button>
                ) : (
                  <Button 
                    variant="ghost"
                    size="sm"
                    onClick={handleLogin}
                    className="text-white bg-white/10 hover:bg-white/20 rounded-full px-3 py-1 text-sm font-medium transition-all duration-200 hover:scale-105"
                  >
                    {t('login')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Account Switcher Dropdown */}
      {isClient && !isInitializing && isAuthenticated && (
        <AccountSwitcherDropdown
          buttonRect={buttonRect}
          isOpen={dropdownOpen}
          onClose={handleDropdownClose}
          isDark={isDark}
          onOpenProfilePicture={openProfileModal}
        />
      )}

      {/* Profile Picture Modal */}
      {isClient && (
        <ProfilePictureModal
          isOpen={profileModalOpen}
          onClose={closeProfileModal}
        />
      )}
    </>
  );
};

export default Header;